import {
  Component,
  Host,
  Prop,
  h,
  Element,
  Watch,
} from "@stencil/core";

import { StzhDaterangeRanges } from "../../index";

import { StzhDaterangeLocalizedText } from "./stzh-daterange.localization";
import { setPropsIfNull } from "../../utils/utils";

/**
 * @slot from - Slot for datepicker
 * @slot to - Slot for datepicker
 */
@Component({
  tag: "stzh-daterange",
  styleUrl: "stzh-daterange.scss",
  scoped: true
})
export class StzhDaterange {
  /** Translation strings */
  @Prop() localization: StzhDaterangeLocalizedText;

  /** Legend */
  @Prop() legend: string;

  /** Range links (current predefined available ranges: "today", "tomorrow", "next7days") */
  @Prop() rangeLinks: string | StzhDaterangeRanges[];
  private _rangeLinks: StzhDaterangeRanges[] = [];

  @Element() element: HTMLStzhDaterangeElement;

  @Watch("rangeLinks")
  rangeLinksWatcher(newValue: StzhDaterangeRanges[] | string) {
    if (!newValue) {
      this._rangeLinks = [];
    } else if (typeof newValue === "string") {
      try {
        this._rangeLinks = JSON.parse(newValue);
      } catch (e) {
        this._rangeLinks = [];
      }
    } else {
      this._rangeLinks = newValue;
    }
  }

  private observer: MutationObserver;
  private datefromElement: HTMLElement;
  private datetoElement: HTMLElement;

  private handleRangeClick = (type: StzhDaterangeRanges) => {
    let start = new Date();
    let end = new Date();

    if (type === "tomorrow") {
      start.setDate(start.getDate() + 1);
      end.setDate(end.getDate() + 1);
    } else if (type === "next7days") {
      start.setDate(start.getDate());
      end.setDate(end.getDate() + 7);
    }

    const fromDatepicker = this.datefromElement.querySelector('stzh-datepicker');
    const toDatepicker = this.datetoElement.querySelector('stzh-datepicker');

    fromDatepicker?.setDate(start);
    toDatepicker?.setDate(end);
  }

  async componentWillLoad() {
    this.rangeLinksWatcher(this.rangeLinks);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "daterange");
    }
  }

  private init = () => {
    const fromDatepicker = this.element.querySelector(
      'stzh-datepicker[slot="from"], [slot="from"] stzh-datepicker'
    );

    const toDatepicker = this.element.querySelector(
      'stzh-datepicker[slot="to"], [slot="to"] stzh-datepicker'
    );

    if (fromDatepicker && this.localization) {
      setPropsIfNull(fromDatepicker, {
        label: this.localization.labelFrom,
        hideOptional: true
      } as HTMLStzhDatepickerElement)
    }

    if (toDatepicker&& this.localization) {
      setPropsIfNull(toDatepicker, {
        label: this.localization.labelTo,
        hideOptional: true
      } as HTMLStzhDatepickerElement)
    }
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-daterange": true,
      "stzh-daterange--has-links": this._rangeLinks.length > 0
    };

    const translations = {
      "today": this.localization.rangeToday,
      "tomorrow": this.localization.rangeTomorrow,
      "next7days": this.localization.rangeNextSevenDays
    }

    return (
      <Host>
        <fieldset class={classes}>
          <legend class="stzh-daterange__legend">
            {this.legend || this.localization.legend}
          </legend>

          <div
            class="stzh-daterange__datefrom"
            ref={(el) => (this.datefromElement = el as HTMLDivElement)}
          >
            <slot name="from"></slot>
          </div>
          <div
            class="stzh-daterange__dateto"
            ref={(el) => (this.datetoElement = el as HTMLDivElement)}
          >
            <slot name="to"></slot>
          </div>
          <div class="stzh-daterange__links">
            {this._rangeLinks.map((type) =>
              <stzh-link
                label={translations[type]}
                onClick={() => this.handleRangeClick(type)}
              ></stzh-link>
            )}
          </div>
        </fieldset>
      </Host>
    );
  }
}
